import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Group,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ProjectDetailDto } from '@ledokol/contracts'
import { updateProject } from '../../shared/api/projects'
import { fetchUsers } from '../../shared/api/users'
import { fetchFunnelSegments } from '../../shared/api/funnel-segments'
import { fetchDepartments } from '../../shared/api/departments'
import { useAuthStore } from '../auth/store'
import { DateOnlyPicker } from './date-only-picker'

interface Props {
  project: ProjectDetailDto
}

const LEAD_SOURCE_OPTS = [
  { value: 'referral', label: 'Реферал / рекомендация' },
  { value: 'cold_call', label: 'Холодный звонок' },
  { value: 'inbound', label: 'Входящий запрос' },
  { value: 'tender', label: 'Тендер' },
  { value: 'other', label: 'Другое' },
]

const CURRENCY_OPTS = [
  { value: 'UZS', label: 'UZS — сум' },
  { value: 'USD', label: 'USD' },
  { value: 'RUB', label: 'RUB' },
  { value: 'EUR', label: 'EUR' },
]

export function OverviewTab({ project }: Props) {
  const qc = useQueryClient()
  const { user: me } = useAuthStore()
  const [budget, setBudget] = useState<string>('')
  const [currency, setCurrency] = useState<string | null>(null)
  const [leadSource, setLeadSource] = useState<string | null>(null)
  const [followUp, setFollowUp] = useState<string>('') // YYYY-MM-DD
  const [nps, setNps] = useState<number | ''>('')
  const [resultNotes, setResultNotes] = useState('')
  const [leadUserId, setLeadUserId] = useState<string | null>(null)
  const [segmentId, setSegmentId] = useState<string | null>(null)
  // Сразу из проекта (не []), иначе на первом кадре мигнёт «Нужен хотя бы один отдел».
  const [departmentIds, setDepartmentIds] = useState<string[]>(() => project.departments.map((d) => d.id))

  const segmentsQ = useQuery({ queryKey: ['funnel-segments'], queryFn: fetchFunnelSegments })

  // RBAC лида: admin/director всегда; head только своего отдела (см. projects.assertCanModify).
  // employee/account → read-only (API всё равно вернёт 403; блокируем сразу).
  const projectDeptIds = useMemo(
    () => new Set(project.departments.map((d) => d.id)),
    [project],
  )
  const canEditLead =
    me?.role === 'admin' ||
    me?.role === 'director' ||
    (me?.role === 'head' && me.departments.some((d) => projectDeptIds.has(d.id)))

  // RBAC отделов (вариант B, зеркало projects.assertCanModify): убрать отдел — только
  // admin/director; добавить — ещё head отдела проекта и account-лид. Остальным — read-only.
  const canEditProject = !!me?.permissions.includes('projects.edit')
  const canRemoveDepts = canEditProject && (me?.role === 'admin' || me?.role === 'director')
  const canAddDepts =
    canRemoveDepts ||
    (canEditProject &&
      ((me?.role === 'head' && me.departments.some((d) => projectDeptIds.has(d.id))) ||
        (me?.role === 'account' && project.lead.id === me.id)))
  const deptsQ = useQuery({ queryKey: ['departments'], queryFn: fetchDepartments, enabled: canAddDepts })
  // Текущие отделы проекта у тех, кто только добавляет, — disabled: Mantine не рисует у них «×»
  // и не даёт снять выбор в списке.
  const deptOptions = useMemo(
    // Пока справочник грузится — отделы проекта, чтобы у выбранных «пилюль» были подписи.
    () => (deptsQ.data ?? project.departments).map((d) => ({ value: d.id, label: d.name, disabled: !canRemoveDepts && projectDeptIds.has(d.id) })),
    [deptsQ.data, project.departments, canRemoveDepts, projectDeptIds],
  )
  const onDepartmentsChange = (v: string[]) =>
    // Backspace в MultiSelect снимает и disabled-значение — возвращаем текущие отделы назад.
    setDepartmentIds(canRemoveDepts ? v : [...project.departments.map((d) => d.id).filter((id) => !v.includes(id)), ...v])
  const deptsChanged =
    departmentIds.length !== projectDeptIds.size || departmentIds.some((id) => !projectDeptIds.has(id))

  // Список кандидатов в лида — все активные не-удалённые сотрудники.
  // Здесь специально без фильтра по роли: бывает, что лидом ставят head'а или
  // даже account'а — решение продуктовое, UI не сужает.
  const usersQ = useQuery({
    queryKey: ['users', { active: true }],
    queryFn: () => fetchUsers({ active: true }),
    enabled: canEditLead,
  })

  const leadOptions = useMemo(() => {
    const list = usersQ.data ?? []
    return list.map((u) => ({
      value: u.id,
      // Label включает @tg_username — Mantine Select с searchable фильтрует по label,
      // значит поиск работает и по ФИО, и по нику.
      label: u.tg_username ? `${u.full_name} — @${u.tg_username}` : u.full_name,
    }))
  }, [usersQ.data])

  useEffect(() => {
    // budget_minor — это BigInt-как-строка в минорных единицах (× 100).
    // Делим на 100 для отображения в "целых" сумах.
    setBudget(project.budget_minor ? (Number(project.budget_minor) / 100).toString() : '')
    setCurrency(project.budget_currency ?? null)
    setLeadSource(project.lead_source ?? null)
    setFollowUp(project.next_follow_up_at ?? '')
    setNps(project.nps_score ?? '')
    setResultNotes(project.result_notes ?? '')
    setLeadUserId(project.lead.id)
    setSegmentId(project.segment?.id ?? null)
    setDepartmentIds(project.departments.map((d) => d.id))
  }, [project])

  const saveMut = useMutation({
    mutationFn: () => {
      const budgetMinor = budget ? String(Math.round(Number(budget) * 100)) : null
      return updateProject(project.id, {
        // lead_user_id отправляем только если у юзера есть права И значение изменилось.
        // Иначе backend на 'head не своего отдела' выдаст 403 на любое сохранение,
        // даже когда менялся только бюджет.
        ...(canEditLead && leadUserId && leadUserId !== project.lead.id && { lead_user_id: leadUserId }),
        // departments — по тому же принципу: только при наличии прав и реальной смене.
        ...(canAddDepts && deptsChanged && { departments: departmentIds }),
        budget_minor: budgetMinor,
        budget_currency: currency,
        lead_source: leadSource,
        next_follow_up_at: followUp || null,
        nps_score: nps === '' ? null : (nps as number),
        result_notes: resultNotes || null,
        segment_id: segmentId,
      })
    },
    onSuccess: () => {
      notifications.show({ message: 'Сохранено', color: 'green' })
      qc.invalidateQueries({ queryKey: ['project', project.id] })
      qc.invalidateQueries({ queryKey: ['projects'] })
    },
    onError: (e: unknown) => {
      const msg = (e instanceof Error && e.message) || 'Не удалось сохранить'
      notifications.show({ message: msg, color: 'red' })
    },
  })

  return (
    <Stack gap="md" maw={700}>
      {canEditLead ? (
        <Select
          label="Лид проекта"
          description="Поиск по ФИО или @telegram-нику"
          placeholder="Выберите сотрудника…"
          data={leadOptions}
          value={leadUserId}
          onChange={setLeadUserId}
          searchable
          nothingFoundMessage={usersQ.isLoading ? 'Загрузка…' : 'Не найдено'}
          allowDeselect={false}
        />
      ) : (
        <Box>
          <Text size="sm" c="dimmed" mb={4}>Лид проекта</Text>
          <Text>
            {project.lead.full_name}
            {/* read-only ветка: показываем @ник если есть, чтобы было видно «кто это в TG» */}
          </Text>
        </Box>
      )}

      {canAddDepts ? (
        <MultiSelect
          label="Отделы"
          description={
            canRemoveDepts
              ? 'Сотрудники отделов видят проект и его задачи'
              : 'Можно добавить отдел; убрать — только админ или директор'
          }
          placeholder={departmentIds.length === 0 ? 'Выберите отделы…' : undefined}
          data={deptOptions}
          value={departmentIds}
          onChange={onDepartmentsChange}
          error={departmentIds.length === 0 ? 'Нужен хотя бы один отдел' : undefined}
          searchable
          nothingFoundMessage={deptsQ.isLoading ? 'Загрузка…' : 'Не найдено'}
        />
      ) : (
        <Box>
          <Text size="sm" c="dimmed" mb={4}>Отделы</Text>
          <Text>{project.departments.map((d) => d.name).join(', ') || '—'}</Text>
        </Box>
      )}

      <Group grow>
        <TextInput
          label="Бюджет (целые единицы)"
          type="number"
          value={budget}
          onChange={(e) => setBudget(e.currentTarget.value)}
          description="Например: 1500000 = 1.5 млн"
        />
        <Select
          label="Валюта"
          data={CURRENCY_OPTS}
          value={currency}
          onChange={setCurrency}
          clearable
        />
      </Group>

      <Select
        label="Сегмент воронки"
        description="Температура клиента для рассылок (справочник правит admin)"
        placeholder="Не назначен"
        data={(segmentsQ.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
        value={segmentId}
        onChange={setSegmentId}
        clearable
      />

      <Group grow>
        <Select
          label="Источник лида"
          data={LEAD_SOURCE_OPTS}
          value={leadSource}
          onChange={setLeadSource}
          clearable
        />
        <DateOnlyPicker
          label="Следующий контакт"
          value={followUp || null}
          onChange={(v) => setFollowUp(v ?? '')}
        />
      </Group>

      <NumberInput
        label="NPS (0-10)"
        value={nps}
        onChange={(v) => setNps(typeof v === 'number' ? v : '')}
        min={0}
        max={10}
        description="Оценка удовлетворённости клиента после флайта"
      />

      <Textarea
        label="Результат / заметки по флайту"
        value={resultNotes}
        onChange={(e) => setResultNotes(e.currentTarget.value)}
        autosize
        minRows={3}
        maxRows={8}
      />

      <Group justify="flex-end">
        <Button loading={saveMut.isPending} disabled={departmentIds.length === 0} onClick={() => saveMut.mutate()}>
          Сохранить
        </Button>
      </Group>
    </Stack>
  )
}
